import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Target, Trophy, Database, Cross, KeyRound, Grid3x3, Brain,
  Hash, PawPrint, Repeat, Star, Infinity as InfinityIcon, Dices, ListOrdered,
  Calculator, Layers, Search, BarChart3, MapPin, User, Users, CreditCard,
  ShoppingBag, QrCode, LifeBuoy, LogOut, Bot, Crown, FileSpreadsheet,
  LogIn, UserPlus,
} from "lucide-react";
import { isAndroidTWA } from "@/utils/runtimeContext";

interface MobileMenuSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface MenuItem {
  label: string;
  icon: React.ElementType;
  path: string;
  color: string;
  authRequired?: boolean;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const sections: MenuSection[] = [
  {
    title: "✨ Descobrir",
    items: [
      { label: "Guru Chat 🆕", icon: Bot, path: "/guru-chat", color: "text-amber-500", authRequired: true },
      { label: "Vitrine 🎯", icon: Sparkles, path: "/vitrine", color: "text-yellow-500" },
    ],
  },
  {
    title: "🎯 Jogos & IA",
    items: [
      { label: "Gerar IA", icon: Sparkles, path: "/gerar-jogos", color: "text-yellow-500", authRequired: true },
      { label: "Puxadas", icon: PawPrint, path: "/puxadas", color: "text-orange-500", authRequired: true },
      { label: "Loterias", icon: Target, path: "/loterias", color: "text-green-500", authRequired: true },
      { label: "Software", icon: FileSpreadsheet, path: "/software", color: "text-blue-500", authRequired: true },
    ],
  },
  {
    title: "🔮 Métodos Especiais",
    items: [
      { label: "C. Matadora 🆕", icon: Target, path: "/metodos?method=centena-matadora", color: "text-red-600", authRequired: true },
      { label: "Desdobr. JB", icon: Layers, path: "/metodos?method=desdobramento-jb", color: "text-sky-500", authRequired: true },
      { label: "Bicho AI", icon: Bot, path: "/metodos?method=sistema-ia-bicho", color: "text-fuchsia-500", authRequired: true },
      { label: "Cruz do Dia", icon: Cross, path: "/metodos?method=cruz-dia", color: "text-red-500", authRequired: true },
      { label: "Chave Ouro", icon: KeyRound, path: "/metodos?method=calculadora-chave-ouro", color: "text-amber-500", authRequired: true },
      { label: "7 Grupos", icon: Grid3x3, path: "/metodos?method=7-grupos", color: "text-purple-500", authRequired: true },
      { label: "Bio Numérico", icon: Brain, path: "/metodos?method=bionumerico-stellar", color: "text-pink-500", authRequired: true },
      { label: "Numerologia", icon: Hash, path: "/metodos?method=numerologia-nome", color: "text-indigo-500", authRequired: true },
      { label: "Centena Bichos", icon: PawPrint, path: "/metodos?method=centena-bichos", color: "text-emerald-500", authRequired: true },
      { label: "C. Viciadas", icon: Repeat, path: "/metodos?method=centenas-viciadas", color: "text-cyan-500", authRequired: true },
      { label: "Dezenas Pr.", icon: Star, path: "/metodos?method=dezenas-premiadas", color: "text-yellow-600", authRequired: true },
      { label: "Infalível", icon: InfinityIcon, path: "/metodos?method=esquema-infalivel", color: "text-rose-500", authRequired: true },
      { label: "Método 777", icon: Dices, path: "/metodos?method=metodo-777", color: "text-violet-500", authRequired: true },
      { label: "Sequência", icon: ListOrdered, path: "/metodos?method=metodo-sequencia", color: "text-teal-500", authRequired: true },
      { label: "Soma Bahia", icon: Calculator, path: "/metodos?method=soma-chave-ouro", color: "text-orange-600", authRequired: true },
      { label: "Achar Cent.", icon: Search, path: "/metodos?method=achar-centena-dia", color: "text-lime-500", authRequired: true },
    ],
  },
  {
    title: "📊 Resultados & Análise",
    items: [
      { label: "Result. RIO", icon: Trophy, path: "/resultados-bicho", color: "text-amber-500" },
      { label: "Estatísticas", icon: BarChart3, path: "/estatisticas", color: "text-blue-500", authRequired: true },
    ],
  },
  {
    title: "👤 Conta",
    items: [
      { label: "Minha Conta", icon: User, path: "/minha-conta", color: "text-slate-500", authRequired: true },
      { label: "Afiliados", icon: Users, path: "/afiliados", color: "text-green-600", authRequired: true },
      { label: "Planos", icon: Crown, path: "/planos", color: "text-yellow-500" },
      { label: "Loja", icon: ShoppingBag, path: "/loja", color: "text-pink-500" },
      { label: "Pagamento", icon: QrCode, path: "/pagamento", color: "text-emerald-600" },
      { label: "Suporte", icon: LifeBuoy, path: "/suporte", color: "text-blue-600" },
    ],
  },
];

const MobileMenuSheet = ({ open, onOpenChange }: MobileMenuSheetProps) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const inTWA = isAndroidTWA();

  // Inside Android TWA, hide ALL purchase-related entries (Planos, Loja, Pagamento)
  // to comply with Google Play billing policies. Use neutral wording only.
  const PURCHASE_PATHS = new Set(["/planos", "/pagamento", "/loja"]);
  const visibleSections = sections.map((section) => ({
    ...section,
    items: inTWA
      ? section.items.filter((item) => !PURCHASE_PATHS.has(item.path))
      : section.items,
  })).filter((section) => section.items.length > 0);

  const handleNavigate = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.warn("Falha no logout (ignorada):", err);
    } finally {
      onOpenChange(false);
      navigate("/");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[85vh] rounded-t-2xl p-0 flex flex-col"
      >
        <SheetHeader className="px-4 pt-4 pb-2 border-b">
          <SheetTitle className="text-left text-lg">
            Todas as Ferramentas
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
          {!user && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                🔐 Acesso
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleNavigate("/login")}
                  aria-label="Entrar"
                  className="flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-600 text-white shadow-md active:scale-95 transition-all min-h-[56px]"
                >
                  <LogIn className="w-5 h-5" />
                  <span className="text-sm font-bold">Entrar</span>
                </button>
                <button
                  onClick={() => handleNavigate("/cadastro")}
                  aria-label="Cadastrar"
                  className="flex items-center justify-center gap-2 p-3 rounded-xl bg-primary text-primary-foreground active:scale-95 transition-all min-h-[56px]"
                >
                  <UserPlus className="w-5 h-5" />
                  <span className="text-sm font-bold">Cadastrar</span>
                </button>
              </div>
            </div>
          )}
          {visibleSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                {section.title}
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.label}
                      onClick={() => handleNavigate(item.path)}
                      aria-label={item.label}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-xl bg-muted/40 hover:bg-muted active:scale-95 transition-all min-h-[72px]"
                    >
                      <Icon className={`w-6 h-6 ${item.color}`} />
                      <span className="text-[10px] font-medium text-center leading-tight line-clamp-2">
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {user && (
            <div className="pt-2 pb-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-destructive/10 text-destructive hover:bg-destructive/20 active:scale-95 transition-all"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-semibold">Sair</span>
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenuSheet;
